import type { QueryDocumentSnapshot } from 'firebase/firestore';

import type { FireSnapshot } from '@elara/firebolt/FireCacheManager';
import type { AnyModelSpec } from '@elara/firebolt/shared';

import type { ClientModelInstance, MakeExtensions, ModelExtensions } from './types.js';

import { FirestoreCache } from '@elara/firebolt/FireCacheManager';

import { createClientInstance } from '#firebase/createClientInstance.js';
import { valueToFirestore } from '#firebase/utils.js';

/**
 * @internal
 * @function createClientDataConverter
 * Creates a stateful `FirestoreDataConverter` for a given `ModelSpec`.
 * This converter is the core of the client-side data layer, integrating with the `FireCacheManager`
 * to ensure that each Firestore document is represented by a single, unique, and reactive object instance in the application.
 * @param spec - The `ModelSpec` for the model.
 * @param makeExtensions - A function that creates the extensions for the model instance, allowing for custom methods and computed properties.
 * @returns The configured Firestore data converter.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createClientDataConverter<Spec extends AnyModelSpec, Extensions extends ModelExtensions<Spec>>(
  spec: Spec,
  makeExtensions: MakeExtensions<Spec, Extensions>,
  postProcessor?: (data: Spec['DataType']) => Spec['DataType'],
) {
  return {
    fromFireSnapshot(snap: FireSnapshot<Spec['DataType']>): ClientModelInstance<Spec, Extensions> {
      return FirestoreCache.acquire(snap, (snap) => createClientInstance(snap, snap.data(), makeExtensions));
    },
    fromFirestore(snap: QueryDocumentSnapshot<Spec['InputType']>): ClientModelInstance<Spec, Extensions> {
      let dbData: Spec['DataType'] = spec.read(snap.data());
      if (postProcessor) {
        dbData = postProcessor(dbData);
      }
      const convertedSnap: FireSnapshot<Spec['DataType']> = { id: snap.id, ref: snap.ref, data: () => dbData };
      return this.fromFireSnapshot(convertedSnap);
    },
    toFirestore(modelObject: ClientModelInstance<Spec, Extensions>): Spec['DataType'] {
      const toFirestore = modelObject.toFirestore;
      const toJSON = (modelObject as { toJSON?: (() => unknown) | undefined }).toJSON;
      const dbData =
        typeof toFirestore === 'function'
          ? toFirestore.call(modelObject)
          : typeof toJSON === 'function'
            ? toJSON.call(modelObject)
            : modelObject;
      return valueToFirestore(dbData) as Spec['DataType'];
    },
  };
}
